import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion } from 'motion/react'
import HeroTube from '@/components/generals/hero-tube'
import HeroObject from '@/components/generals/hero-object'
import RevealWords from '@/components/landing/reveal-words'

const EASE = [0.22, 1, 0.36, 1] as const

interface PageHeroProps {
    // Stable section id. The headline gets `${id}-title`.
    id: string
    label: string
    title: string
    intro: string
    // Tint of the disc behind the 3D tube, for example bg-blush. The tube sits in its own column, never behind the headline.
    tint: string
    // Which 3D piece the page shows. Each page has its own, all in the same light and motion.
    object?: 'tube' | 'jar' | 'stick' | 'drop'
    // Buttons under the intro.
    children?: ReactNode
}

const OBJECT_LABELS = {
    jar: 'An open jar of Softly All-Over Balm',
    stick: 'A Softly balm stick with its cap lifting off',
    drop: 'The Softly drop'
} as const

// The opening band of every inner page: bracketed label, script headline, intro and the real 3D tube, on the hero's apricot gradient.
const PageHero: FC<PageHeroProps> = ({ id, label, title, intro, tint, object = 'tube', children }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)

    return (
        <section
            ref={sectionRef}
            id={id}
            aria-labelledby={`${id}-title`}
            className='relative overflow-hidden bg-[linear-gradient(180deg,var(--peach)_0%,#F6D7C1_58%,var(--apricot)_100%)]'
        >
            <div className='relative mx-auto grid max-w-[1440px] items-center gap-12 px-5 pb-20 pt-36 lg:grid-cols-12 lg:gap-10 lg:px-10 lg:pb-28 lg:pt-44'>
                <div className='lg:col-span-7'>
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: EASE }}
                        className='text-[13px] font-medium uppercase tracking-[0.2em] text-cocoa/80'
                    >
                        [ {label} ]
                    </motion.p>

                    <RevealWords
                        as='h1'
                        id={`${id}-title`}
                        text={title}
                        className='mt-5 max-w-[11em] text-balance type-display'
                    />

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
                        className='mt-6 max-w-[34rem] text-[17px] leading-relaxed text-cocoa/85 lg:text-[18px]'
                    >
                        {intro}
                    </motion.p>

                    {children && (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.2, delay: 0.7, ease: EASE }}
                            className='mt-9 flex flex-wrap gap-3'
                        >
                            {children}
                        </motion.div>
                    )}
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.6, delay: 0.3, ease: EASE }}
                    className='lg:col-span-5'
                >
                    {object === 'tube' ? (
                        <HeroTube sectionRef={sectionRef} tint={tint} label='Softly All-Over Balm: a cobalt blue squeeze tube with a white cap' />
                    ) : (
                        <HeroObject sectionRef={sectionRef} variant={object} tint={tint} label={OBJECT_LABELS[object]} />
                    )}
                </motion.div>
            </div>
        </section>
    )
}

export default PageHero
